import React, { useEffect, useRef, useState } from 'react'
import { Card } from 'antd'
import dayjs from 'dayjs'
import {
  dateFormatYearMonthDay,
  downLoadFileBlob,
  formatMessageNew,
  initValueAndLabel,
  paginationInit,
  uniqueFun,
  formatStartTime,
  formatEndTime
} from '@/utils/common'
import {
  getSpecialSkuReportExport,
  getSpecialSkuReportPageReport,
  getSpecialSkuReportSummary
} from '@/services/rfid/reports/specialSkuReport'
import SearchBar from '@/components/searchbar/SearchBar'
import AntdTable from '@/components/table/AntdTable'
import { CARTON_PE, REWORK_CHECK_RESULT_STATUS } from '@/utils/types'
import ActionBar from '../../../components/table/ActionBar'
import { modalError } from '../../../utils/modalUtils'
import { useSelector } from 'react-redux'
import { getPlantList } from '@/services/rfid/rfidConfig/specialSkuConfig'
import { findSpecialCodeAndType } from '@/services/rfid/rfidConfig/specialTypeConfig'

// 固定为 OB，页面不再提供 Business Type 查询条件
const OB_BUSINESS_TYPE = 'OB'
const startMonth = dayjs().subtract(1, 'day')
const endMonth = dayjs().subtract(1, 'day')
const MAX_SEARCH_INPUT_COUNT = 50
const UNKNOWN_SKU_CODE = 'UNKNOWN_SKU'

const defaultSummaryTableList = [
  {
    validationResult: 'Latest Validation Result',
    accurate: 0,
    epcShortage: 0,
    epcOverage: 0,
    upcMismatched: 0,
    newCarton: 0,
    manualConfirm: 0,
    total: 0
  },
  {
    validationResult: 'Actual Validation Result',
    accurate: 0,
    epcShortage: 0,
    epcOverage: 0,
    upcMismatched: 0,
    newCarton: 0,
    manualConfirm: 0,
    total: 0
  }
]
const ObSpecialSkuReport = () => {
  const [summaryTableList, setSummaryTableList] = useState([])
  const [detailTableList, setDetailTableList] = useState([])
  const [queryFlag, setQueryFlag] = useState(false)
  const [pagination, setPagination] = useState(paginationInit)
  // 列过滤 state（多选下拉，值为 Y/N）
  const [specialSkuFilter, setSpecialSkuFilter] = useState([])
  const mounting = useRef(false)
  const languageState = useSelector(state => state.commonStore.language)
  const safeSummaryTableList = Array.isArray(summaryTableList) ? summaryTableList : []
  const displaySummaryTableList = safeSummaryTableList.length > 0 ? safeSummaryTableList : defaultSummaryTableList

  const bingResultConfig = {
    1: { key: 'languageObj.specialSkuReport.accurate' },
    2: { key: 'languageObj.specialSkuReport.epcOverage' },
    3: { key: 'languageObj.specialSkuReport.epcShortage' },
    4: { key: 'languageObj.specialSkuReport.upcMismatched' },
    5: { key: 'languageObj.specialSkuReport.Unexpected' },
    6: { key: 'languageObj.specialSkuReport.manualConfirm' },
    7: { key: 'languageObj.specialSkuReport.newCarton' }
  }

  const renderValidationResult = val => {
    const actualVal = val === null ? 7 : val
    const cfg =
      actualVal !== undefined && actualVal !== null && bingResultConfig[actualVal] ? bingResultConfig[actualVal] : null
    const style = (cfg && cfg.style) || {}
    const text =
      cfg && cfg.key
        ? formatMessageNew(cfg.key)
        : actualVal !== undefined && actualVal !== null
        ? String(actualVal)
        : '--'
    return <span style={style}>{text}</span>
  }

  const parseToArray = val => {
    if (Array.isArray(val)) {
      return val.map(v => (v === null || v === undefined ? '' : String(v).trim())).filter(v => v !== '')
    }
    if (val === null || val === undefined || val === '') return []
    return String(val)
      .split(/[,\n;，\s]+/)
      .map(v => v.trim())
      .filter(v => v !== '')
  }

  const renderMismatchedSku = value => {
    if (value === null || value === undefined || value === '') return value
    return String(value)
      .split(',')
      .map(item => {
        const trimmed = item.trim()
        return trimmed === UNKNOWN_SKU_CODE ? formatMessageNew('languageObj.specialSkuReport.unknownSku') : trimmed
      })
      .join(',')
  }

  let formRef = useRef(null)

  const validateSearchParams = info => {
    const carton = (info.cartonNumber || '').toString().trim()
    const asnDn = (info.asnDn || '').toString().trim()
    const gr = info.grDate

    const hasCarton = carton !== ''
    const hasAsnDn = asnDn !== ''
    const hasGr = Array.isArray(gr) ? gr.length > 0 && (gr[0] || gr[1]) : !!gr

    if (!hasCarton && !hasAsnDn && !hasGr) {
      return {
        ok: false,
        message: formatMessageNew('languageObj.specialSkuReport.obOpitionError')
      }
    }

    const cartonList = parseToArray(info.cartonNumber)
    if (cartonList.length > MAX_SEARCH_INPUT_COUNT) {
      return {
        ok: false,
        message: formatMessageNew('languageObj.specialSkuReport.cartonNumberMaxError')
      }
    }

    const asnDnList = parseToArray(info.asnDn)
    if (asnDnList.length > MAX_SEARCH_INPUT_COUNT) {
      return {
        ok: false,
        message: formatMessageNew('languageObj.specialSkuReport.obAsnDnMaxError')
      }
    }

    return { ok: true }
  }

  const searchListInit = [
    {
      type: 'select',
      label: 'languageObj.specialSkuReport.latestValidationResult',
      key: 'latestValidationResult',
      selectList: REWORK_CHECK_RESULT_STATUS,
      mode: 'multiple',
      allowClear: true,
      maxTagCount: 1,
      initialValues: [],
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'select',
      label: 'languageObj.specialSkuReport.plants',
      key: 'plant',
      allowClear: true,
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: [],
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'textarea',
      label: 'languageObj.specialSkuReport.cartonNumber',
      key: 'cartonNumber',
      initialValues: '',
      allowClear: true,
      minRows: 1,
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'textarea',
      label: 'languageObj.specialSkuReport.obAsnDn',
      key: 'asnDn',
      initialValues: '',
      allowClear: true,
      minRows: 1,
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'timeRange',
      label: 'languageObj.specialSkuReport.obGrDate',
      key: 'grDate',
      format: dateFormatYearMonthDay,
      initialValues: [startMonth, endMonth],
      disabledDate: current => {
        if (!current) return false
        const c = dayjs(current).startOf('day')
        const todayEnd = dayjs().endOf('day')
        return c.isAfter(todayEnd)
      },
      allowClear: true,
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'textarea',
      label: 'languageObj.specialSkuReport.sku',
      key: 'sku',
      initialValues: '',
      allowClear: true,
      minRows: 1,
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'select',
      label: 'languageObj.specialSkuReport.specialType',
      key: 'specialType',
      allowClear: true,
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: [],
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'select',
      label: 'languageObj.specialSkuReport.actualValidationResult',
      key: 'actualValidationResult',
      selectList: REWORK_CHECK_RESULT_STATUS,
      mode: 'multiple',
      allowClear: true,
      maxTagCount: 1,
      initialValues: [],
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    },
    {
      type: 'select',
      label: 'languageObj.specialSkuReport.cartonPe',
      key: 'cartonPeList',
      selectList: CARTON_PE,
      mode: 'multiple',
      allowClear: true,
      spans: { xl: 8, xxl: 8 },
      isdefaultfilter: 'true'
    }
  ]

  const [searchList, setSearchList] = useState(searchListInit)

  const tableColumns = [
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.obAsnDn'),
      dataIndex: 'asnNumber',
      width: 150,
      fixed: 'left'
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.cartonNumber'),
      dataIndex: 'cartonNumber',
      width: 160,
      fixed: 'left'
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.obAsnDnSku'),
      dataIndex: 'asnSku',
      width: 120
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.specialSku'),
      dataIndex: 'specialSku',
      width: 120,
      filters: [
        { text: 'Y', value: 'Y' },
        { text: 'N', value: 'N' }
      ],
      filteredValue: Array.isArray(specialSkuFilter) && specialSkuFilter.length ? specialSkuFilter : null,
      filterMultiple: true
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.obAsnDnQty'),
      dataIndex: 'asnQty',
      width: 100
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.scanQty'),
      dataIndex: 'scanQty',
      width: 100
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.deltaQty'),
      width: 100,
      dataIndex: 'deltaQty'
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.latestSystemValidationResult'),
      dataIndex: 'latestValidationResult',
      width: 220,
      render: renderValidationResult
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.actualValidationResult'),
      dataIndex: 'actualValidationResult',
      width: 220,
      render: renderValidationResult
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.obGrDate'),
      dataIndex: 'grDate',
      width: 150
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.plants'),
      dataIndex: 'plant',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.productName'),
      dataIndex: 'productName',
      width: 200
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.specialType'),
      dataIndex: 'specialType',
      width: 140
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.exceptionType'),
      dataIndex: 'exceptionType',
      width: 140
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.cartonPe'),
      dataIndex: 'cartonPe',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.misMatchedSku'),
      dataIndex: 'mismatchedSku',
      render: renderMismatchedSku,
      width: 120
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.misMatchedQty'),
      dataIndex: 'mismatchedQty',
      width: 120
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.lastScannedDevice'),
      dataIndex: 'lastScannedDeviceCode',
      width: 180
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.lastScanTime'),
      dataIndex: 'lastScanTime',
      width: 150
    }
  ]

  const summaryColumns = [
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.validationResult'),
      dataIndex: 'validationResult',
      width: 120,
      render: val => {
        if (val === 'Latest Validation Result') {
          return formatMessageNew('languageObj.specialSkuReport.latestValidationResult')
        }
        if (val === 'Actual Validation Result') {
          return formatMessageNew('languageObj.specialSkuReport.actualValidationResult')
        }
        return val
      }
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.accurate'),
      dataIndex: 'accurate',
      width: 100
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.epcShortage'),
      dataIndex: 'epcShortage',
      width: 100
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.epcOverage'),
      dataIndex: 'epcOverage',
      width: 100
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.upcMismatched'),
      dataIndex: 'upcMismatched',
      width: 120
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.newCarton'),
      dataIndex: 'newCarton',
      width: 100
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.manualConfirm'),
      dataIndex: 'manualConfirm',
      width: 120
    },
    {
      title: () => formatMessageNew('languageObj.specialSkuReport.total'),
      dataIndex: 'total',
      width: 100
    }
  ]

  useEffect(() => {
    mounting.current && searchDataFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFlag])

  useEffect(() => {
    findPlantModulesFun()
    loadSpecialTypeOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const findPlantModulesFun = () => {
    getPlantList().then(res => {
      const arr = Array.isArray(res.resultData) ? res.resultData : []
      const plantOptions = initValueAndLabel(uniqueFun(arr, 3))
      setSearchList(prev => {
        const next = [...prev]
        next.forEach(item => {
          if (item.key === 'plant') {
            item.selectList = plantOptions
          }
        })
        return next
      })
    })
  }

  const loadSpecialTypeOptions = () => {
    findSpecialCodeAndType().then(res => {
      const rawDrop = res.resultData
      const resDt = Array.isArray(rawDrop) ? rawDrop : []
      const specialTypeOptions = initValueAndLabel(
        uniqueFun(
          resDt.map(row => `${row.specialCode}-${row.specialType}`),
          2
        )
      )
      setSearchList(prev => {
        const next = [...prev]
        next.forEach(item => {
          if (item.key === 'specialType') {
            item.selectList = specialTypeOptions
          }
        })
        return next
      })
    })
  }

  const downLoadFun = () => {
    const info = (formRef.current && formRef.current.getSearchValue && formRef.current.getSearchValue()) || {}
    const valid = validateSearchParams(info)
    if (!valid.ok) {
      modalError({ content: valid.message })
      return
    }
    const params = {
      isChinese: languageState === 'zh_CN' ? true : false,
      ...getParams()
    }
    getSpecialSkuReportExport(params)
      .then(res => {
        downLoadFileBlob(res)
      })
      .catch(err => {
        modalError({
          content: err.errorMessage
        })
      })
  }

  const searchDataBtn = () => {
    const info = (formRef.current && formRef.current.getSearchValue && formRef.current.getSearchValue()) || {}
    const valid = validateSearchParams(info)
    if (!valid.ok) {
      modalError({ content: valid.message })
      return
    }

    mounting.current = true
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    setQueryFlag(!queryFlag)
  }

  const searchClearDataBtn = () => {
    mounting.current = true
    formRef && formRef.current.clearForm()
    setSummaryTableList([])
    setDetailTableList([])
    // 同时清空列过滤条件
    setSpecialSkuFilter([])
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    setQueryFlag(!queryFlag)
  }

  const getParams = () => {
    const info = (formRef.current && formRef.current.getSearchValue()) || {}
    const { grDate } = info
    const normalizeMultiSelect = val => {
      if (val === null || val === undefined || val === '') return []
      return Array.isArray(val) ? val : [val]
    }

    return {
      actualValidationResults: normalizeMultiSelect(info.actualValidationResult),
      asnNumbers: parseToArray(info.asnDn),
      businessType: OB_BUSINESS_TYPE,
      cartonNumbers: parseToArray(info.cartonNumber),
      grDateFrom: formatStartTime(grDate),
      grDateTo: formatEndTime(grDate),
      latestValidationResults: normalizeMultiSelect(info.latestValidationResult),
      plants: normalizeMultiSelect(info.plant),
      skus: parseToArray(info.sku),
      specialTypes: Array.isArray(info.specialType) ? info.specialType : info.specialType ? [info.specialType] : [],
      cartonPeList: info.cartonPeList,
      // Y/N 多选值传给后端
      specialSku: Array.isArray(specialSkuFilter) ? specialSkuFilter : []
    }
  }

  const searchDataFun = () => {
    const params = getParams()
    const pageParams = {
      ...params,
      pageNo: pagination.currentPage,
      size: pagination.pageSize
    }
    Promise.all([getSpecialSkuReportSummary(params), getSpecialSkuReportPageReport(pageParams)])
      .then(([summaryRes, pageRes]) => {
        if (summaryRes && summaryRes.resultData) {
          const summaryData = summaryRes.resultData
          setSummaryTableList(summaryData || [])
        }
        if (pageRes && pageRes.resultData) {
          const dt = pageRes.resultData
          const records = dt.records || (dt.detail && dt.detail.records) || []
          const total = dt.total ?? (dt.detail && dt.detail.total) ?? 0
          setDetailTableList(records)
          setPagination({
            ...pagination,
            total
          })
        }
      })
      .catch(err => {
        modalError({
          content: err.errorMessage
        })
      })
  }

  // 处理表格分页和列过滤变化（Antd Table 会传 pagination、filters、sorter）
  const handleTableChange = (pagina, filters) => {
    // 同步列过滤 state（specialSku）
    if (filters && Object.prototype.hasOwnProperty.call(filters, 'specialSku')) {
      const v = filters.specialSku || []
      setSpecialSkuFilter(Array.isArray(v) ? v : [])
    }

    const params = {
      ...pagination,
      currentPage: pagina.current,
      pageSize: pagina.pageSize
    }
    setPagination({ ...params })
    setQueryFlag(flag => !flag)
  }

  const renderSummaryTable = () => {
    const thStyle = {
      border: '1px solid #ddd',
      padding: 10,
      textAlign: 'center',
      background: '#fafafa',
      fontSize: 16
    }
    const tdStyle = {
      border: '1px solid #eee',
      padding: 10,
      textAlign: 'center',
      fontSize: 16,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }

    const getColumnTitle = col => (typeof col.title === 'function' ? col.title() : col.title)

    const getCellValue = (row, col, rowIndex) => {
      const val = row[col.dataIndex]
      if (col.render) {
        return col.render(val, row, rowIndex)
      }
      return val != null ? val : 0
    }

    return (
      <div
        className="summary-table"
        style={{ display: 'flex', justifyContent: 'center', marginTop: 20, marginBottom: 20 }}
      >
        <table
          style={{
            borderCollapse: 'collapse',
            width: '80%',
            fontSize: 16,
            tableLayout: 'fixed'
          }}
        >
          <thead>
            <tr>
              {summaryColumns.map(col => (
                <th key={col.dataIndex} style={{ ...thStyle, width: col.width }}>
                  {getColumnTitle(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displaySummaryTableList.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {summaryColumns.map((col, colIndex) => (
                  <td
                    key={col.dataIndex}
                    style={{
                      ...tdStyle,
                      fontWeight: colIndex === 0 || col.dataIndex === 'total' ? 600 : undefined
                    }}
                  >
                    {getCellValue(row, col, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div>
      <Card bordered={false} bodyStyle={{ padding: 4 }}>
        <div className="search-bar">
          <SearchBar
            ref={formRef}
            searchList={searchList}
            showSearchBtn={true}
            showClearBtn={true}
            onSearch={searchDataBtn}
            onClear={searchClearDataBtn}
          />
        </div>

        {renderSummaryTable()}

        <div className="content-container">
          <ActionBar onDownload={downLoadFun} style={{ alignItems: 'end' }} />
          <AntdTable
            dataSource={detailTableList}
            columns={tableColumns}
            columnsKey="obSpecialSkuReportColumns"
            pagination={pagination}
            onChange={handleTableChange}
          />
        </div>
      </Card>
    </div>
  )
}

export default ObSpecialSkuReport
